import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import { FC } from 'react';
import { AsyncExportStatusResponse, BillingExportStatus } from '../../interfaces/billing.async';

interface AsyncExportProgressProps {
  status: AsyncExportStatusResponse | null;
  isLoading: boolean;
  isPolling: boolean;
  error: Error | null;
  onDownloadOutput?: () => void;
  onDownloadErrors?: () => void;
  onReset?: () => void;
}

type ProgressColor = 'primary' | 'success' | 'warning' | 'error' | 'inherit';

const getProgressColor = (status: string): ProgressColor => {
  switch (status) {
    case BillingExportStatus.Completed:
      return 'success';
    case BillingExportStatus.CompletedWithErrors:
      return 'warning';
    case BillingExportStatus.Failed:
      return 'error';
    default:
      return 'primary';
  }
};

export const AsyncExportProgress: FC<AsyncExportProgressProps> = ({
  status,
  isLoading,
  isPolling,
  error,
  onDownloadOutput,
  onDownloadErrors,
  onReset,
}) => {
  // Error state
  if (error) {
    return (
      <Alert
        severity='error'
        sx={{ mt: 2 }}
        action={
          onReset && (
            <Button color='inherit' size='small' onClick={onReset}>
              Try Again
            </Button>
          )
        }>
        <AlertTitle>Export Failed</AlertTitle>
        {error.message}
      </Alert>
    );
  }

  // Loading/starting state
  if (!status && isLoading) {
    return (
      <Alert severity='info' sx={{ mt: 2 }} icon={false}>
        <Typography variant='body2' sx={{ mb: 1 }}>
          Starting export...
        </Typography>
        <LinearProgress />
      </Alert>
    );
  }

  if (!status) return null;

  const isTerminal = [
    BillingExportStatus.Completed,
    BillingExportStatus.CompletedWithErrors,
    BillingExportStatus.Failed,
  ].includes(status.status as BillingExportStatus);

  const hasOutputFile = status.result?.outputFileUrl || status.result?.outputFileContent;
  const hasErrorFile = status.result?.errorFileUrl || status.result?.errorFileContent;

  // Failed state
  if (status.status === BillingExportStatus.Failed) {
    return (
      <Alert
        severity='error'
        sx={{ mt: 2 }}
        icon={<ErrorIcon />}
        action={
          onReset && (
            <Button color='inherit' size='small' onClick={onReset}>
              Try Again
            </Button>
          )
        }>
        <AlertTitle>Export Failed</AlertTitle>
        {status.errorMessage || 'An error occurred during export.'}
      </Alert>
    );
  }

  // Completed with errors state
  if (status.status === BillingExportStatus.CompletedWithErrors) {
    return (
      <Alert severity='warning' sx={{ mt: 2 }} icon={<WarningIcon />}>
        <AlertTitle>Completed with Errors</AlertTitle>
        <Typography variant='body2' sx={{ mb: 1 }}>
          Processed {status.processedRows} of {status.totalRows} rows. {status.errorRows} rows had
          errors.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
          {hasOutputFile && onDownloadOutput && (
            <Button
              variant='contained'
              size='small'
              color='primary'
              startIcon={<DownloadIcon />}
              onClick={onDownloadOutput}>
              Download Output
            </Button>
          )}
          {hasErrorFile && onDownloadErrors && (
            <Button
              variant='outlined'
              size='small'
              color='warning'
              startIcon={<DownloadIcon />}
              onClick={onDownloadErrors}>
              Download Error Report
            </Button>
          )}
          {onReset && (
            <Button variant='text' size='small' onClick={onReset}>
              New Export
            </Button>
          )}
        </Box>
      </Alert>
    );
  }

  // Completed successfully
  if (status.status === BillingExportStatus.Completed) {
    return (
      <Alert severity='success' sx={{ mt: 2 }} icon={<CheckCircleIcon />}>
        <AlertTitle>Export Completed</AlertTitle>
        <Typography variant='body2' sx={{ mb: 1 }}>
          Successfully processed {status.processedRows} rows.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
          {hasOutputFile && onDownloadOutput && (
            <Button
              variant='contained'
              size='small'
              color='success'
              startIcon={<DownloadIcon />}
              onClick={onDownloadOutput}>
              Download Output
            </Button>
          )}
          {onReset && (
            <Button variant='text' size='small' onClick={onReset}>
              New Export
            </Button>
          )}
        </Box>
      </Alert>
    );
  }

  // Processing state (pending or processing)
  return (
    <Alert severity='info' sx={{ mt: 2 }} icon={false}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant='body2'>
          Processing: {status.processedRows} / {status.totalRows} rows (
          {Math.round(status.progress)}%)
        </Typography>
      </Box>
      <LinearProgress
        variant={isPolling && !isTerminal ? 'indeterminate' : 'determinate'}
        value={status.progress}
        color={getProgressColor(status.status)}
        sx={{ height: 8, borderRadius: 1 }}
      />
    </Alert>
  );
};

export default AsyncExportProgress;
